/**
 * Compile-time gate for development tooling.
 *
 * A URL can NEVER enable debug tooling on its own. The rule is:
 *
 *     debugActive = buildAllowsDevTools && queryAsksForDebug
 *
 * `__DEV_TOOLS_ALLOWED__` is replaced at build time by Vite (`define`):
 *
 * | Build                          | Value   |
 * | ------------------------------ | ------- |
 * | `vite` dev server              | `true`  |
 * | `vite build --mode e2e`        | `true`  |
 * | `vite build` (production)      | `false` |
 *
 * Because it becomes a literal `false` in production, every guarded branch is
 * dead code and the bundler strips it. Verified absent from `dist`:
 * `DevPanel` and its markup, the `kill-ship` / `toggle-rapid-fire` commands,
 * the FPS chip and the `window.__KENO_DEV__` hook, plus the object literals
 * that would set `debugBossDelaySeconds` / `devAttackSpeedBoost`.
 *
 * What legitimately remains in `dist` is inert: the frozen
 * `{debug:false, bossFast:false, rapidFire:false}` literal, and the *reads*
 * of those options inside `@kenocyber/game-domain` (a platform-neutral package
 * that cannot depend on a web build flag). With nothing able to write them,
 * the production boss roll is always the real 3-16 minute one.
 *
 * Guard style matters: always compare the constant directly
 * (`DEV_TOOLS_ALLOWED && x`), never pass it through a variable, or the bundler
 * loses the ability to fold the branch away.
 */
export const DEV_TOOLS_ALLOWED: boolean = __DEV_TOOLS_ALLOWED__;

export interface DevFlags {
  /** Debug UI + diagnostics (FPS chip, dev panel, E2E hook). */
  readonly debug: boolean;
  /** DEV ONLY: boss spawns in ~10 s instead of the 3-16 min production roll. */
  readonly bossFast: boolean;
  /**
   * DEV ONLY: attack-speed boost, for exercising very high cadence.
   *
   * Named `rapidFire` because that is all it does. KENOCYBER never converts
   * shots into lasers (see `CLAUDE.md`), so even at this cadence every shot is
   * a discrete projectile with its own damage number.
   */
  readonly rapidFire: boolean;
}

/** Everything off. The only possible result when the build forbids tooling. */
export const NO_DEV_FLAGS: DevFlags = Object.freeze({
  debug: false,
  bossFast: false,
  rapidFire: false,
});

/**
 * Pure flag reader.
 *
 * `buildAllowsDevTools` is passed explicitly rather than read from the module
 * constant so the disabled path is directly testable.
 */
export function readDevFlags(search: string, buildAllowsDevTools: boolean): DevFlags {
  if (!buildAllowsDevTools) {
    return NO_DEV_FLAGS;
  }
  const params = new URLSearchParams(search);
  const debug = params.get('debug') === '1';
  if (!debug) {
    return NO_DEV_FLAGS;
  }
  return {
    debug: true,
    bossFast: params.get('bossFast') === '1',
    rapidFire: params.get('rapidFire') === '1',
  };
}
